/**
 * UnlockPage — the SMS door-unlock link's landing page.
 *
 * Unlinked and unauthenticated: nothing in the app navigates here, and there is no session.
 * A member gets here by tapping a link in a text a few minutes before their booking, and the
 * token in that link is the only credential.
 *
 * Differences from the mobile `/access` page, all deliberate:
 *
 * - **A button, not slide-to-unlock.** Product's call.
 * - **No geofence.** A geolocation permission prompt on a page reached from a text message is
 *   a poor first impression, and the check enforces nothing anyway — it is client-side, fails
 *   open, and no endpoint reads coordinates (`accessControl/KNOWN_ISSUES.md` #7). A
 *   fast-follow audit will decide whether it should exist at all.
 * - **No keypad branch and no "not available yet" state.** Links are only issued for
 *   unlock-mode clubs once the window is open, and `resolveLink` refuses everything else
 *   server-side.
 * - **The Bay PIN after the door** at a SimLock club (simulator-companion D77), as on mobile:
 *   "Show Bay PIN" on the door step, or by itself once every door opens.
 * - **No navigation.** There is nowhere for an unauthenticated visitor to go.
 *
 * @see /apps/api/src/services/accessControl/bookingLinks/README.md
 */

import { useState } from 'react';
import { useParams } from 'react-router';
import { Button } from '@butter/ui/primitives/actions';
import { DoorIcon, ErrorIcon, LoaderIcon } from '@butter/ui/icons';
import {
  AccessWindowNote,
  BayPinPanel,
  DoorAccessPanel,
  doorTitle,
  unlockButtonLabel,
  unlockPrompt,
  unlockedTitle,
  UNLOCK_AGAIN,
  UNLOCK_FAILED,
  BACK_TO_DOOR,
  SHOW_BAY_PIN,
} from '@butter/ui/domains/access';
import { AccessLinkError, useUnlockLink } from '~/services/internalApi/access';

/** Shared chrome — no header, no nav; there is nothing here to navigate to. */
function Shell({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-screen flex-col bg-white">{children}</div>;
}

export function UnlockPage() {
  const { token: urlToken } = useParams<{ token: string }>();
  const { missingToken, access, isLoading, error, retry, unlock, unlockResult, dismissResult } =
    useUnlockLink(urlToken);
  // Chosen on the door step. A link names one booking, so a flag is enough.
  const [pinChosen, setPinChosen] = useState(false);

  // Opened directly, or the tab was restored after sessionStorage was cleared.
  if (missingToken) {
    return (
      <Shell>
        <DoorAccessPanel
          icon={<ErrorIcon className="size-10 text-white" />}
          title="Open the link from your text"
          subtitle="This page needs the link we texted you. Open that message and tap the link again."
        />
      </Shell>
    );
  }

  // Held until the exchange resolves rather than rendering a skeleton: with no tenant in the
  // URL the page has no brand yet, and the unlock button is theme-coloured. One spinner, no
  // flash of the wrong colour.
  if (isLoading) {
    return (
      <Shell>
        <div className="flex-1 flex items-center justify-center px-6">
          <LoaderIcon className="size-10 text-theme-400 animate-spin" />
        </div>
      </Shell>
    );
  }

  // Every server-side REFUSAL — expired, revoked, cancelled booking, club switched off —
  // arrives as one message on purpose. Distinguishing them for an anonymous caller would be
  // an oracle for probing which tokens exist.
  //
  // A server FAULT is a different thing and gets different copy. The refusal copy is final by
  // design — it tells the member not to bother — so showing it for a transient 5xx sends
  // someone away from a door their link would have opened. Now that a DB error answers 500
  // rather than hanging the socket, this is a state a member can actually reach.
  if (error || !access) {
    const linkError = error instanceof AccessLinkError ? error : null;
    const serverFault = linkError?.isServerFault ?? false;

    return (
      <Shell>
        <DoorAccessPanel
          icon={<ErrorIcon className="size-10 text-white" />}
          title={serverFault ? 'Something went wrong' : "This link isn't active"}
          // `serverMessage`, not `error.message`: a refusal's message comes from the envelope
          // and is written for a member, but a transport failure's is the browser's own
          // `"Failed to fetch"`. Printing that under "This link isn't active" told someone
          // standing at a door that their working link was dead.
          subtitle={
            serverFault
              ? "We couldn't reach the door just now. Please try again."
              : (linkError?.serverMessage ??
                'This link works from shortly before your booking until it ends.')
          }
          // The one recoverable case, and the only one worth a button: a refusal will refuse
          // again, but a fault may not.
          action={
            serverFault ? (
              <Button variant="themeOutline" className="w-full" onClick={() => retry()}>
                Try again
              </Button>
            ) : undefined
          }
        />
      </Shell>
    );
  }

  const { doors, accessWindowMinutes, bayPin } = access;
  const allOpened = Boolean(unlockResult && unlockResult.failed.length === 0);

  // The Bay PIN — chosen, or reached once every door opened.
  if (bayPin && (pinChosen || allOpened)) {
    return (
      <Shell>
        <BayPinPanel
          bayPin={bayPin}
          announce={allOpened}
          status={allOpened ? unlockResult?.message : undefined}
          action={
            <Button
              variant="themeOutline"
              className="w-full"
              onClick={() => {
                setPinChosen(false);
                dismissResult();
              }}
            >
              {BACK_TO_DOOR}
            </Button>
          }
        />
      </Shell>
    );
  }

  // Someone may already have opened the door.
  const showBayPin = bayPin ? (
    <Button variant="themeOutline" className="w-full mt-3" onClick={() => setPinChosen(true)}>
      {SHOW_BAY_PIN}
    </Button>
  ) : null;

  // Every door opened. Not a latch — doors re-lock and the token is good for the whole
  // booking, so the member can come straight back to the actionable state.
  if (unlockResult && unlockResult.failed.length === 0) {
    return (
      <Shell>
        <DoorAccessPanel
          announce
          title={unlockedTitle(unlockResult.unlocked.length)}
          subtitle={unlockResult.message}
          action={
            <>
              <Button variant="themeOutline" className="w-full" onClick={dismissResult}>
                {UNLOCK_AGAIN}
              </Button>
              {showBayPin}
            </>
          }
        />
      </Shell>
    );
  }

  return (
    <Shell>
      <DoorAccessPanel
        icon={<DoorIcon className="w-10 h-10 text-white" />}
        title={doorTitle(doors)}
        subtitle={unlockPrompt(doors, 'Tap', bayPin !== null)}
        // Partial success: the request succeeded and at least one door opened, so this is
        // guidance rather than an error — and the button stays live for the one that didn't.
        status={unlockResult && unlockResult.failed.length > 0 ? unlockResult.message : undefined}
        // The same rule as the refusal screen above, and the likelier path to hit it: the
        // member is already here and tapping. A dropped connection mid-unlock used to print
        // the browser's `"Failed to fetch"` in red under the button.
        error={
          unlock.isError
            ? ((unlock.error instanceof AccessLinkError
                ? unlock.error.serverMessage
                : undefined) ?? UNLOCK_FAILED)
            : undefined
        }
        action={
          <>
            <Button
              variant="theme"
              size="xl"
              className="w-full"
              onClick={() => unlock.mutate()}
              disabled={unlock.isPending}
            >
              {unlockButtonLabel(doors, unlock.isPending)}
            </Button>
            {showBayPin}
          </>
        }
        footer={<AccessWindowNote minutes={accessWindowMinutes} />}
      />
    </Shell>
  );
}
